import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { CenteredLoader } from "@/components/shared/Loader";

/**
 * AuthGuard protects every route that needs a signed-in user.
 *
 * It holds the render until bootstrap has finished, then either renders the
 * route or sends the user to /login carrying where they were headed, so
 * GuestGuard can put them back there once they sign in.
 *
 * `authChecked` is part of the wait, not just `isLoading`. Auth state is
 * persisted, so on a reload Redux rehydrates `isAuthenticated` from the last
 * session before the app has asked the server anything. Gating on `isLoading`
 * alone let the guard act on that rehydrated value in the gap before the
 * bootstrap thunk starts — rendering the app for a session that had ended, or
 * bouncing to /login on one that had not.
 */
const AuthGuard = () => {
  const { isAuthenticated, isLoading, authChecked } = useAuth();
  const location = useLocation();

  if (isLoading || !authChecked) {
    return (
      <div className="min-h-screen bg-background-primary">
        <CenteredLoader />
      </div>
    );
  }

  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace state={{ from: location }} />;
};

export default AuthGuard;
